/**
 * M-02 日報・警告。未解決／対応済みを切り替えて確認し、対応済みとして記録する。
 */

import { issueLabel } from '@meisui/core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, EmptyState, ErrorMessage, Loading, Notice } from '../../components/ui.js';
import { currentMonth, formatDateLabel, formatDateTime } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function IssuesPage(): React.JSX.Element {
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState<'unresolved' | 'resolved'>('unresolved');
  const [staffFilter, setStaffFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const state = useAsync(() => endpoints.issues(month, tab === 'resolved'), [month, tab]);

  const items = (state.data?.items ?? []).filter(
    (issue) =>
      (staffFilter === '' || issue.employeeName === staffFilter) &&
      (codeFilter === '' || issue.code === codeFilter),
  );
  const staffNames = [
    ...new Set((state.data?.items ?? []).map((issue) => issue.employeeName ?? '')),
  ].filter(Boolean);
  const codes = [...new Set((state.data?.items ?? []).map((issue) => issue.code))];

  const resolve = async (issueId: string): Promise<void> => {
    setBusy(issueId);
    setActionError(null);
    try {
      await endpoints.resolveIssue(issueId, note[issueId] ?? '');
      state.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <MonthNav month={month} onChange={setMonth} />

      <Notice tone="info">
        「対応済みにする」は警告を消すのではなく、確認したことを記録します。未解決の警告が残っている月は確定できません。
      </Notice>

      <ErrorMessage error={state.error} />
      <ErrorMessage error={actionError} />
      {state.loading && !state.data && <Loading />}

      {state.data && (
        <Card
          title={`確認事項 ${items.length}件`}
          action={
            <div className="flex flex-wrap gap-2">
              <div className="flex gap-1">
                <button
                  type="button"
                  className={`btn btn--sm ${tab === 'unresolved' ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={() => setTab('unresolved')}
                >
                  未解決
                </button>
                <button
                  type="button"
                  className={`btn btn--sm ${tab === 'resolved' ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={() => setTab('resolved')}
                >
                  対応済み
                </button>
              </div>
              <select
                className="input w-auto"
                value={staffFilter}
                onChange={(event) => setStaffFilter(event.target.value)}
                aria-label="スタッフで絞り込む"
              >
                <option value="">すべてのスタッフ</option>
                {staffNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                className="input w-auto"
                value={codeFilter}
                onChange={(event) => setCodeFilter(event.target.value)}
                aria-label="種別で絞り込む"
              >
                <option value="">すべての種別</option>
                {codes.map((code) => (
                  <option key={code} value={code}>
                    {issueLabel(code)}
                  </option>
                ))}
              </select>
            </div>
          }
        >
          {items.length === 0 ? (
            <EmptyState
              message={
                tab === 'unresolved'
                  ? '未解決の確認事項はありません。'
                  : '対応済みの記録はありません。'
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>スタッフ</th>
                    <th>対象日</th>
                    <th>種別</th>
                    <th>内容</th>
                    {tab === 'unresolved' ? <th>対応</th> : <th>対応日時・記録</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((issue) => (
                    <tr key={issue.id}>
                      <td className="whitespace-nowrap font-semibold">
                        {issue.employeeName ?? '—'}
                        <span className="ml-1 text-[0.75rem] font-normal text-ink-muted">
                          {issue.department}
                        </span>
                      </td>
                      <td className="whitespace-nowrap">
                        <Link
                          className="underline"
                          to={`/admin/calculation/${issue.employeeId}/${issue.date}`}
                        >
                          {formatDateLabel(issue.date)}
                        </Link>
                      </td>
                      <td>
                        <span
                          className={`pill ${issue.severity === 'error' ? 'pill--danger' : 'pill--warn'}`}
                        >
                          {issue.severity === 'error' ? 'エラー' : '確認'}
                        </span>
                        <span className="ml-1 whitespace-nowrap text-[0.8rem]">
                          {issueLabel(issue.code)}
                        </span>
                      </td>
                      <td>{issue.message}</td>
                      {tab === 'unresolved' ? (
                        <td>
                          <div className="flex items-center gap-2">
                            <input
                              className="input w-48"
                              type="text"
                              placeholder="確認内容（任意）"
                              value={note[issue.id] ?? ''}
                              onChange={(event) =>
                                setNote((previous) => ({
                                  ...previous,
                                  [issue.id]: event.target.value,
                                }))
                              }
                            />
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm whitespace-nowrap"
                              disabled={busy === issue.id}
                              onClick={() => void resolve(issue.id)}
                            >
                              対応済みにする
                            </button>
                          </div>
                        </td>
                      ) : (
                        <td className="text-ink-muted">
                          {formatDateTime(issue.resolvedAt)}
                          {issue.resolutionNote && `／${issue.resolutionNote}`}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
